import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, LinearProgress, Typography } from "@mui/material";

import { UIEmptyState, UITable } from "../../../ui";
import UIStatusTag from "../../../ui/UIStatusTag";
import { formatDate } from "../../../../utils/Helpers";
import { EVALUATION_ROUND_STATUSES } from "./constants";

const statusToneMap = {
  [EVALUATION_ROUND_STATUSES.running]: "warningSoft",
  [EVALUATION_ROUND_STATUSES.completed]: "successSoft",
};

const EvaluationRoundsTable = ({ rows, labels, onRowClick, isFiltered }) => {
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: labels.round,
        minWidth: 140,
        sortable: false,
      },
      {
        field: "applicantsCount",
        headerName: labels.applicantsNo,
        minWidth: 130,
        sortable: false,
      },
      {
        field: "startDate",
        headerName: labels.startDate,
        minWidth: 130,
        sortable: false,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "endDate",
        headerName: labels.endDate,
        minWidth: 130,
        sortable: false,
        renderCell: ({ value }) => (value ? formatDate(value) : "—"),
      },
      {
        field: "status",
        headerName: labels.status,
        minWidth: 130,
        sortable: false,
        renderCell: ({ value }) => (
          <UIStatusTag
            status={value}
            label={labels.statuses[value] || value}
            tone={statusToneMap[value]}
            preset="table"
          />
        ),
      },
      {
        field: "progress",
        headerName: labels.progress,
        minWidth: 200,
        flex: 1.5,
        sortable: false,
        renderCell: ({ row }) => {
          const pct =
            row.applicantsCount > 0
              ? Math.round((row.evaluatedCount / row.applicantsCount) * 100)
              : 0;
          const barColor =
            row.status === EVALUATION_ROUND_STATUSES.running
              ? "var(--color-warning-default)"
              : "var(--color-success-default)";
          return (
            <Box className="eval-rounds__progress-cell">
              <LinearProgress
                variant="determinate"
                value={pct}
                className="eval-rounds__progress-bar"
                sx={{
                  "& .MuiLinearProgress-bar": { backgroundColor: barColor },
                }}
              />
              <Typography variant="caption" className="eval-rounds__progress-label">
                <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{row.evaluatedCount} / {row.applicantsCount}</span>
              </Typography>
            </Box>
          );
        },
      },
    ],
    [labels],
  );

  if (!rows.length) {
    return (
      <UIEmptyState
        title={isFiltered ? labels.noResultsTitle : labels.emptyTitle}
        description={isFiltered ? labels.noResultsDescription : labels.emptyDescription}
      />
    );
  }

  return (
    <UITable
      rows={rows}
      columns={columns}
      contentSized
      fillAvailableWidth
      hideFooter
      onRowClick={({ row }) => onRowClick(row)}
      sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
    />
  );
};

EvaluationRoundsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.shape({
    round: PropTypes.string.isRequired,
    applicantsNo: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    progress: PropTypes.string.isRequired,
    emptyTitle: PropTypes.string.isRequired,
    emptyDescription: PropTypes.string.isRequired,
    noResultsTitle: PropTypes.string.isRequired,
    noResultsDescription: PropTypes.string.isRequired,
    statuses: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  onRowClick: PropTypes.func.isRequired,
  isFiltered: PropTypes.bool,
};

EvaluationRoundsTable.defaultProps = {
  isFiltered: false,
};

export default EvaluationRoundsTable;
